/**
 * 通知一覧パネル（通知ベルの中身）
 *
 * デスクトップの Popover とモバイルの BottomSheet で共用する。
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChatBubbleLeftIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import {
  useMarkNotificationRead,
  useNotificationList,
} from '@/hooks/useNotifications';
import type { AppNotification } from '@/services/appNotifications';
import {
  APP_NOTIFICATION_TYPE,
  type AppNotificationType,
} from '@/constants/notificationTypes';
import { formatRelativeTime } from '@/utils/datetime';

const TYPE_ICON_CONFIG: Record<
  AppNotificationType,
  { icon: React.ComponentType<{ className?: string }>; colorClass: string }
> = {
  [APP_NOTIFICATION_TYPE.PARTICIPANT_JOINED]: {
    icon: UserPlusIcon,
    colorClass: 'text-secondary-600 bg-secondary-50',
  },
  [APP_NOTIFICATION_TYPE.PARTICIPANT_UPDATED]: {
    icon: PencilSquareIcon,
    colorClass: 'text-primary-600 bg-primary-50',
  },
  [APP_NOTIFICATION_TYPE.COMMENT_ADDED]: {
    icon: ChatBubbleLeftIcon,
    colorClass: 'text-primary-600 bg-primary-50',
  },
  [APP_NOTIFICATION_TYPE.EVENT_CHANGED]: {
    icon: ArrowPathIcon,
    colorClass: 'text-warning-600 bg-warning-50',
  },
  [APP_NOTIFICATION_TYPE.EVENT_CANCELLED]: {
    icon: ExclamationTriangleIcon,
    colorClass: 'text-danger-600 bg-danger-50',
  },
  [APP_NOTIFICATION_TYPE.SYSTEM_ANNOUNCEMENT]: {
    icon: MegaphoneIcon,
    colorClass: 'text-accent-600 bg-accent-50',
  },
};

interface NotificationPanelProps {
  open: boolean;
  unreadCount: number;
  /** 通知クリックや「すべて既読」後にパネルを閉じたいときに呼ばれる */
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  unreadCount,
  onClose,
}) => {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useNotificationList(open);
  const { markOne, markAll } = useMarkNotificationRead();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // 本文つきのお知らせはタップでインライン展開して全文を読む
  // （リンクありでも行タップでは遷移せず、展開内の「詳しく見る」から遷移）
  const isExpandable = (notification: AppNotification) =>
    notification.type === APP_NOTIFICATION_TYPE.SYSTEM_ANNOUNCEMENT &&
    !!notification.body;

  const handleItemClick = (notification: AppNotification) => {
    if (!notification.read) {
      markOne.mutate(notification.notificationId);
    }
    if (isExpandable(notification)) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(notification.notificationId)) {
          next.delete(notification.notificationId);
        } else {
          next.add(notification.notificationId);
        }
        return next;
      });
      return;
    }
    if (notification.linkPath) {
      onClose();
      navigate(notification.linkPath);
    }
  };

  const handleDetailLink = (linkPath: string) => {
    onClose();
    navigate(linkPath);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-text-100">
        <h2 className="text-sm font-semibold text-text-900">お知らせ</h2>
        <button
          type="button"
          onClick={() => markAll.mutate()}
          disabled={unreadCount === 0 || markAll.isPending}
          className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:text-text-300 disabled:cursor-default"
        >
          すべて既読
        </button>
      </div>

      <div className="overflow-y-auto max-h-[60vh] sm:max-h-[70vh]">
        {isLoading && (
          <div className="py-10 flex justify-center">
            <span className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {isError && !isLoading && (
          <p className="py-10 text-center text-sm text-text-500">
            通知の取得に失敗しました
          </p>
        )}
        {!isLoading && !isError && notifications.length === 0 && (
          <p className="py-10 text-center text-sm text-text-500">
            新しいお知らせはありません
          </p>
        )}

        <ul>
          {notifications.map((notification) => {
            const { icon: Icon, colorClass } =
              TYPE_ICON_CONFIG[notification.type] ??
              TYPE_ICON_CONFIG[APP_NOTIFICATION_TYPE.SYSTEM_ANNOUNCEMENT];
            const expandable = isExpandable(notification);
            const expanded =
              expandable && expandedIds.has(notification.notificationId);
            return (
              <li
                key={notification.notificationId}
                className={notification.read ? '' : 'bg-primary-50/60'}
              >
                <button
                  type="button"
                  onClick={() => handleItemClick(notification)}
                  aria-expanded={expandable ? expanded : undefined}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-text-50"
                >
                  <span
                    className={`mt-0.5 flex-shrink-0 rounded-full p-1.5 ${colorClass}`}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-sm leading-snug ${
                        notification.read
                          ? 'text-text-700'
                          : 'font-medium text-text-900'
                      }`}
                    >
                      {notification.title}
                    </span>
                    {notification.body && !expanded && (
                      // 注意: line-clamp-2 は display:-webkit-box を設定するため
                      // block を併用すると上書きされて clamp が効かなくなる
                      <span className="mt-0.5 text-xs text-text-500 line-clamp-2 whitespace-pre-wrap">
                        {notification.body}
                      </span>
                    )}
                    <span className="mt-0.5 block text-xs text-text-400">
                      {formatRelativeTime(
                        notification.publishAt ?? notification.createdAt
                      )}
                    </span>
                  </span>
                  {!notification.read && (
                    <span
                      className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500"
                      aria-label="未読"
                    />
                  )}
                  {expandable && (
                    <ChevronDownIcon
                      className={`mt-2 h-4 w-4 flex-shrink-0 text-text-400 transition-transform ${
                        expanded ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </button>
                {expanded && (
                  <div className="pl-14 pr-4 pb-3">
                    <p className="text-xs leading-relaxed text-text-600 whitespace-pre-wrap">
                      {notification.body}
                    </p>
                    {notification.linkPath && (
                      <button
                        type="button"
                        onClick={() => handleDetailLink(notification.linkPath!)}
                        className="mt-2 text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        詳しく見る →
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {hasNextPage && (
          <div className="border-t border-text-100 p-2">
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="w-full rounded-lg py-2 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:text-text-300"
            >
              {isFetchingNextPage ? '読み込み中…' : 'さらに表示'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
